// ============================================================
// Onglet « Crashs » d'une instance : rapports de crash et erreurs de la console des 10 derniers
// jours, lus sur le CICD (CrashWatchResource). Le CICD garde une WebSocket de console par serveur
// surveillé ; ce service ne fait que lire ses rapports.
//   env : 'dev' (envs de dev, slots Bêta), 'preprod' ou 'prod' ; identifier : serveur Pterodactyl.
// ============================================================
import { apiFetch } from './api';

export type CrashEnv = 'dev' | 'preprod' | 'prod';

/** crash, watchdog, oom, coupure : comptés ; arret-propre : arrêt décidé par le serveur, pas compté. */
export type CrashKind = 'crash' | 'watchdog' | 'oom' | 'coupure' | 'arret-propre';
export type CrashOutcome = 'redemarre' | 'arrete' | 'relance-ratee' | 'inconnu';

export type WatchStateCode = 'active' | 'reconnecting' | 'pending' | 'gone' | 'off-config' | 'off-global' | 'off-env';

export interface WatchState {
  state: WatchStateCode;
  label: string;
  connectedSince?: string | null;
  lastEventAt?: string | null;
  analyzedLines?: number;
  droppedLines?: number;
}

export interface CrashSummary {
  id: string;
  at: string;
  kind: CrashKind;
  counted: boolean;
  outcome: CrashOutcome;
  previousState?: string | null;
  exitCode?: number | null;
  outOfMemory?: boolean | null;
  memoryPercent?: number | null;
  /** La cause en une ligne, en français (le message technique cité tel quel). */
  cause: string;
  /** Fil soupçonné par le Watchdog (« nom : ligne de pile »). */
  suspect?: string | null;
  /** Crashs de plus comptés sur ce rapport (au-delà de 12 par heure). */
  repeats: number;
  lastRepeatAt?: string | null;
}

export interface CrashLine {
  at: string | null;
  text: string;
  /** Reçue après l'arrêt (messages qui suivent, avant la relance). */
  after: boolean;
}

export interface CrashDetail extends CrashSummary {
  lines: CrashLine[];
  /** Index dans `lines` de la ligne qui donne la cause, -1 si aucune. */
  causeIndex: number;
  /** Messages de Wings (code de sortie, mémoire, relance refusée). */
  daemon: string[];
  serverName?: string | null;
}

export interface ErrorGroupSummary {
  id: string;
  title: string;
  level: 'error' | 'fatal';
  /** Occurrences des 10 derniers jours. */
  count: number;
  total: number;
  firstAt: string;
  lastAt: string;
  overflow: boolean;
}

export interface ErrorExample {
  at: string;
  lines: string[];
}

export interface ErrorGroupDetail extends ErrorGroupSummary {
  examples: ErrorExample[];
  pattern?: string;
}

export interface CrashCounts {
  crashes24h: number;
  graceful24h: number;
  newErrors24h: number;
}

export interface CrashBadgeSummary extends CrashCounts {
  lastCrashAt?: string | null;
  watch: WatchState;
}

export interface ServerCrashes extends CrashCounts {
  retentionDays: number;
  crashes: CrashSummary[];
  errors: ErrorGroupSummary[];
  watch: WatchState;
}

export interface CrashWatchSettings {
  configEnabled: boolean;
  globalEnabled: boolean;
  envs: Record<CrashEnv, { enabled: boolean; byDefault: boolean }>;
  canConfigure: boolean;
}

const base = (pid: string, env: CrashEnv, identifier: string) =>
  `/api/projects/${pid}/crash-watch/${env}/servers/${encodeURIComponent(identifier)}`;

export const crashWatchService = {
  summary: (pid: string, env: CrashEnv, identifier: string) =>
    apiFetch<CrashBadgeSummary>(`${base(pid, env, identifier)}/summary`),

  server: (pid: string, env: CrashEnv, identifier: string, withGraceful = false) =>
    apiFetch<ServerCrashes>(`${base(pid, env, identifier)}${withGraceful ? '?graceful=true' : ''}`),

  crash: (pid: string, env: CrashEnv, identifier: string, crashId: string) =>
    apiFetch<CrashDetail>(`${base(pid, env, identifier)}/crashes/${encodeURIComponent(crashId)}`),

  errorGroup: (pid: string, env: CrashEnv, identifier: string, groupId: string) =>
    apiFetch<ErrorGroupDetail>(`${base(pid, env, identifier)}/errors/${encodeURIComponent(groupId)}`),

  settings: (pid: string) => apiFetch<CrashWatchSettings>(`/api/projects/${pid}/crash-watch/settings`),

  setGlobal: (pid: string, enabled: boolean) =>
    apiFetch<CrashWatchSettings>(`/api/projects/${pid}/crash-watch/settings`, { method: 'PUT', json: { global: enabled } }),

  setEnv: (pid: string, env: CrashEnv, enabled: boolean) =>
    apiFetch<CrashWatchSettings>(`/api/projects/${pid}/crash-watch/settings`, { method: 'PUT', json: { env, enabled } }),
};
